
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { crossDomainStorage } from "@/lib/utils/crossDomainStorage";

export function useSuperadminRole() {
  const [isSuperadmin, setIsSuperadmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasNetworkError, setHasNetworkError] = useState<boolean>(false);

  // Check cross-domain storage for superadmin status
  const getSuperadminFromCrossDomain = (): boolean | null => {
    try {
      // First check wcd-website's cross-domain storage
      const sessionData = crossDomainStorage.getSessionData();
      if (sessionData?.user?.isSuperadmin !== undefined) {
        return Boolean(sessionData.user.isSuperadmin);
      }
      
      // Also check africanswhodesign's cross-domain cookie
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const [key, ...valueParts] = cookie.trim().split("=");
        if (key === "CROSS_DOMAIN_SESSION") {
          try {
            const value = valueParts.join("=");
            const crossDomainSession = JSON.parse(decodeURIComponent(value));
            if (crossDomainSession?.is_superadmin !== undefined) {
              return Boolean(crossDomainSession.is_superadmin);
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
      }
    } catch (error) {
      console.warn("[useSuperadminRole] Error reading from cross-domain storage:", error);
    }
    return null;
  };

  // Store superadmin status in cross-domain storage
  const storeSuperadminInCrossDomain = (status: boolean): void => {
    try {
      const sessionData = crossDomainStorage.getSessionData();
      if (sessionData) {
        sessionData.user.isSuperadmin = status;
        crossDomainStorage.setSessionData(sessionData);
      }
    } catch (error) {
      console.warn("[useSuperadminRole] Error storing in cross-domain storage:", error);
    }
  };

  const checkSuperAdminStatus = async (): Promise<boolean> => {
    setIsLoading(true);
    setHasNetworkError(false);
    
    // First, check cross-domain storage for immediate result
    const cachedStatus = getSuperadminFromCrossDomain();
    if (cachedStatus !== null) {
      setIsSuperadmin(cachedStatus);
      setIsLoading(false);
      // Still verify with RPC in background
    }
    
    try {
      const { data, error } = await supabase.rpc("is_superadmin");

      if (error) {
        setHasNetworkError(true);
        // If we have cached status, keep using it
        if (cachedStatus === null) {
          setIsSuperadmin(false);
        }
        return cachedStatus ?? false;
      }

      if (typeof data !== "boolean") {
        return cachedStatus ?? false;
      }
      
      setIsSuperadmin(data);
      // Store in cross-domain storage for future use
      storeSuperadminInCrossDomain(data);
      return data;
    } catch (err) {
      setHasNetworkError(true);
      // If we have cached status, keep using it
      if (cachedStatus === null) {
        setIsSuperadmin(false);
      }
      return cachedStatus ?? false;
    } finally {
      setIsLoading(false);
    }
  };

  const clearCacheAndRecheck = async (): Promise<boolean> => {
    // Clear cross-domain cache
    try {
      const sessionData = crossDomainStorage.getSessionData();
      if (sessionData?.user) {
        delete sessionData.user.isSuperadmin;
        crossDomainStorage.setSessionData(sessionData);
      }
    } catch (error) {
      console.warn("[useSuperadminRole] Error clearing cross-domain cache:", error);
    }
    return await checkSuperAdminStatus();
  };

  const verifyDatabaseSuperadminStatus = async (): Promise<boolean> => {
    // Direct database check - same as checkSuperAdminStatus for now
    return await checkSuperAdminStatus();
  };

  useEffect(() => {
    // Check cross-domain storage first for immediate result
    const cachedStatus = getSuperadminFromCrossDomain();
    if (cachedStatus !== null) {
      setIsSuperadmin(cachedStatus);
      setIsLoading(false);
    }
    
    // Then verify with RPC call
    checkSuperAdminStatus();

    // Listen for cross-domain session updates
    const handleCrossDomainMessage = (event: MessageEvent) => {
      if (
        event.data?.type === "CROSS_DOMAIN_SESSION" &&
        (event.data?.action === "SESSION_LOGIN" ||
          event.data?.action === "SESSION_REFRESH")
      ) {
        // Session updated, check for superadmin status
        const newCachedStatus = getSuperadminFromCrossDomain();
        if (newCachedStatus !== null) {
          setIsSuperadmin(newCachedStatus);
        } else {
          // Re-check if status not in session
          checkSuperAdminStatus();
        }
      }
    };

    window.addEventListener("message", handleCrossDomainMessage);

    return () => {
      window.removeEventListener("message", handleCrossDomainMessage);
    };
  }, []);

  return {
    isSuperadmin,
    isLoading,
    hasNetworkError,
    clearCacheAndRecheck,
    verifyDatabaseSuperadminStatus,
  };
}
